import { AnimatePresence, motion } from 'framer-motion';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface DropdownMenuProps {
  trigger: ReactNode;
  children: ReactNode;
  menuClassName?: string;
}

export const DropdownMenu = ({
  trigger,
  children,
  menuClassName = '',
}: DropdownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  let hoverTimeout: NodeJS.Timeout;

  // Affiche/Cache le menu au survol
  const handleMouseEnter = () => {
    clearTimeout(hoverTimeout);
    calculatePosition();
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    hoverTimeout = setTimeout(() => setIsOpen(false), 300);
  };

  // Affiche/Cache au clic
  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  // Ferme au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calcule la position du dropdown
  const calculatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = menuRef.current?.offsetHeight || 200;

      let top = rect.bottom + window.scrollY;
      const left = Math.min(
        rect.left + window.scrollX,
        window.innerWidth - 200,
      );

      // Si pas assez d'espace en bas, on affiche au-dessus
      if (top + dropdownHeight > window.innerHeight) {
        top = rect.top + window.scrollY - 90;
      }

      setCoords({ top, left });
    }
  };

  // Animation
  const dropdownVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15 } },
  };

  const dropdownContent = isOpen && coords && (
    <motion.div
      ref={menuRef}
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={dropdownVariants}
      style={{ position: 'absolute', top: coords.top, left: coords.left }}
      className={`z-50 ${menuClassName} pointer-events-auto`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={e => e.stopPropagation()}
    >
      <div className="min-w-48 origin-top rounded-md bg-primary shadow-lg ring-1 ring-gray-light">
        <div className="p-1">{children}</div>
      </div>
    </motion.div>
  );

  return (
    <>
      <div
        ref={triggerRef}
        className="w-fit cursor-pointer"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {trigger}
      </div>
      {createPortal(
        <AnimatePresence>{dropdownContent}</AnimatePresence>,
        document.body,
      )}
    </>
  );
};
